import React, { useState } from "react";
import {
  Button,
  Field,
  Input,
  Textarea,
  Text,
  MessageBar,
  MessageBarBody,
  Spinner,
  tokens,
} from "@fluentui/react-components";
import { Delete16Regular, Edit16Regular } from "@fluentui/react-icons";
import { loadRules, upsertRule, deleteRule, generateRuleId } from "../triage-rules";
import type { Rule } from "../triage-rules";
import { runTriage, applyProposals } from "../triage";
import type { TriageProposal, TriageError } from "../triage";
import { resolveSettings } from "../settings";
import type { MailboxAdapter } from "../mailbox-adapter";
import type { OrgPolicy } from "../policy";

export interface RulesPanelProps {
  adapter: MailboxAdapter | null;
  onClose: () => void;
  /**
   * Org policy loaded by `App` and threaded through `ChatPanel`. Resolved via
   * `resolveSettings(policy)` before the "Sort now" triage call so an
   * imposed/locked AI provider config wins over a stored user override — see
   * CLAUDE.md B6/B7 and the governance (gap-5) plan.
   */
  policy?: OrgPolicy;
}

interface RuleFormState {
  id: string | null;
  name: string;
  when: string;
  folder: string;
  categories: string;
}

const EMPTY_FORM: RuleFormState = { id: null, name: "", when: "", folder: "", categories: "" };

function parseCategoriesInput(value: string): string[] | undefined {
  const parsed = value
    .split(",")
    .map((c) => c.trim())
    .filter((c) => c.length > 0);
  return parsed.length > 0 ? parsed : undefined;
}

function describeAction(rule: Rule): string {
  const parts: string[] = [];
  if (rule.action.folder) parts.push(`→ ${rule.action.folder}`);
  if (rule.action.categories && rule.action.categories.length > 0) {
    parts.push(`categories: ${rule.action.categories.join(", ")}`);
  }
  return parts.join(" · ") || "(no action)";
}

/**
 * Rules CRUD (create/update/delete triage assignment rules) plus an on-demand
 * "Sort now" action: batch-classifies recent unread mail against the rules in
 * review mode by default (nothing is written until the user reviews and taps
 * Apply, which routes through `applyProposals`). Move/categorize are
 * non-destructive and are written directly without a confirmation dialog. See
 * docs/superpowers/plans/2026-07-02-gap-4-triage-auto-categorization.md.
 */
export function RulesPanel({ adapter, onClose, policy = {} }: RulesPanelProps) {
  const [rules, setRules] = useState<Rule[]>(() => loadRules());
  const [form, setForm] = useState<RuleFormState>(EMPTY_FORM);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [triaging, setTriaging] = useState(false);
  const [proposals, setProposals] = useState<TriageProposal[] | null>(null);
  const [triageErrors, setTriageErrors] = useState<TriageError[]>([]);
  const [applying, setApplying] = useState(false);
  const [applyFeedback, setApplyFeedback] = useState<{
    intent: "success" | "warning" | "error";
    text: string;
  } | null>(null);

  function startEdit(rule: Rule) {
    setSaveError(null);
    setForm({
      id: rule.id,
      name: rule.name,
      when: rule.when,
      folder: rule.action.folder ?? "",
      categories: (rule.action.categories ?? []).join(", "),
    });
  }

  function cancelEdit() {
    setForm(EMPTY_FORM);
    setSaveError(null);
  }

  async function handleSubmit() {
    if (!form.name.trim() || !form.when.trim()) {
      setSaveError("Name and condition are both required.");
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const rule: Rule = {
        id: form.id ?? generateRuleId(),
        name: form.name.trim(),
        when: form.when.trim(),
        action: {
          folder: form.folder.trim() || undefined,
          categories: parseCategoriesInput(form.categories),
        },
      };
      const next = await upsertRule(rule);
      setRules(next);
      setForm(EMPTY_FORM);
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : "Failed to save rule.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string) {
    setSaveError(null);
    try {
      const next = await deleteRule(id);
      setRules(next);
      if (form.id === id) {
        setForm(EMPTY_FORM);
      }
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : "Failed to delete rule.");
    }
  }

  async function handleSortNow() {
    if (!adapter || triaging) return;
    setTriaging(true);
    setProposals(null);
    setTriageErrors([]);
    setApplyFeedback(null);
    try {
      const settings = resolveSettings(policy);
      const items = await adapter.listEmails("unread");
      const result = await runTriage(items, rules, settings, adapter, { autoApply: false }, policy);
      setProposals(result.proposals);
      setTriageErrors(result.errors);
    } catch (error) {
      setTriageErrors([
        { itemId: "", message: error instanceof Error ? error.message : "Something went wrong." },
      ]);
    } finally {
      setTriaging(false);
    }
  }

  async function handleApplyProposals() {
    if (!adapter || !proposals || proposals.length === 0 || applying) return;
    setApplying(true);
    setApplyFeedback(null);
    try {
      const result = await applyProposals(proposals, adapter);
      setApplyFeedback({
        intent: "success",
        text: `Applied triage to ${result.applied.length} email(s).`,
      });
      setProposals(null);
      if (result.errors.length > 0) {
        setTriageErrors(result.errors);
      }
    } catch (error) {
      setApplyFeedback({
        intent: "error",
        text: error instanceof Error ? error.message : "Something went wrong applying triage.",
      });
    } finally {
      setApplying(false);
    }
  }

  return (
    <div style={{ padding: "16px", overflow: "auto", height: "100vh" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h2 style={{ margin: 0 }}>Triage rules</h2>
        <Button appearance="subtle" onClick={onClose} aria-label="Close">
          ✕
        </Button>
      </div>

      {saveError && (
        <MessageBar intent="error" style={{ marginTop: 8 }}>
          <MessageBarBody>{saveError}</MessageBarBody>
        </MessageBar>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 12 }}>
        {rules.length === 0 && <Text size={200}>No triage rules yet. Add one below.</Text>}
        {rules.map((rule) => (
          <div
            key={rule.id}
            style={{
              border: `1px solid ${tokens.colorNeutralStroke2}`,
              borderRadius: 4,
              padding: 8,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 8,
            }}
          >
            <div>
              <Text weight="semibold">{rule.name}</Text>
              <div>
                <Text size={200}>{rule.when}</Text>
              </div>
              <div>
                <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                  {describeAction(rule)}
                </Text>
              </div>
            </div>
            <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
              <Button
                appearance="subtle"
                icon={<Edit16Regular />}
                aria-label={`Edit ${rule.name}`}
                onClick={() => startEdit(rule)}
              />
              <Button
                appearance="subtle"
                icon={<Delete16Regular />}
                aria-label={`Delete ${rule.name}`}
                onClick={() => void handleDelete(rule.id)}
              />
            </div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 8 }}>
        <Text weight="semibold">{form.id ? "Edit rule" : "New rule"}</Text>
        <Field label="Name">
          <Input
            value={form.name}
            onChange={(_, d) => setForm((f) => ({ ...f, name: d.value }))}
          />
        </Field>
        <Field label="When (natural language condition)">
          <Textarea
            value={form.when}
            rows={2}
            onChange={(_, d) => setForm((f) => ({ ...f, when: d.value }))}
          />
        </Field>
        <Field label="Move to folder (optional)">
          <Input
            value={form.folder}
            onChange={(_, d) => setForm((f) => ({ ...f, folder: d.value }))}
          />
        </Field>
        <Field label="Categories (comma-separated, optional)">
          <Input
            value={form.categories}
            onChange={(_, d) => setForm((f) => ({ ...f, categories: d.value }))}
          />
        </Field>
        <div style={{ display: "flex", gap: 8 }}>
          {saving ? (
            <Spinner size="tiny" />
          ) : (
            <>
              <Button appearance="primary" onClick={() => void handleSubmit()}>
                {form.id ? "Save changes" : "Add rule"}
              </Button>
              {form.id && <Button onClick={cancelEdit}>Cancel</Button>}
            </>
          )}
        </div>
      </div>

      <div style={{ marginTop: 20, borderTop: `1px solid ${tokens.colorNeutralStroke2}`, paddingTop: 12 }}>
        <Text weight="semibold">Sort my inbox now</Text>
        <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8 }}>
          {triaging ? (
            <Spinner size="tiny" label="Classifying…" />
          ) : (
            <Button
              appearance="primary"
              disabled={!adapter || rules.length === 0}
              onClick={() => void handleSortNow()}
            >
              Sort now
            </Button>
          )}
        </div>

        {applyFeedback && (
          <MessageBar intent={applyFeedback.intent} style={{ marginTop: 8 }}>
            <MessageBarBody>{applyFeedback.text}</MessageBarBody>
          </MessageBar>
        )}

        {triageErrors.length > 0 && (
          <MessageBar intent="warning" style={{ marginTop: 8 }}>
            <MessageBarBody>
              {triageErrors.length} email(s) could not be classified: {triageErrors[0].message}
            </MessageBarBody>
          </MessageBar>
        )}

        {proposals && proposals.length === 0 && (
          <Text size={200} style={{ display: "block", marginTop: 8 }}>
            No matching rules found for the checked emails.
          </Text>
        )}

        {proposals && proposals.length > 0 && (
          <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
            {proposals.map((proposal) => (
              <div
                key={proposal.itemId}
                style={{
                  border: `1px solid ${tokens.colorNeutralStroke2}`,
                  borderRadius: 4,
                  padding: 6,
                }}
              >
                <Text size={200} weight="semibold">
                  {proposal.subject}
                </Text>
                <div>
                  <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                    Rule: {proposal.ruleName}
                    {proposal.folder ? ` · → ${proposal.folder}` : ""}
                    {proposal.categories && proposal.categories.length > 0
                      ? ` · categories: ${proposal.categories.join(", ")}`
                      : ""}
                  </Text>
                </div>
              </div>
            ))}
            {applying ? (
              <Spinner size="tiny" label="Applying…" />
            ) : (
              <Button appearance="primary" onClick={() => void handleApplyProposals()}>
                Apply {proposals.length} proposal(s)
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
